import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LangToggle, useI18n } from '../../i18n';
import { useDocumentTitle } from '../../ui/a11y';
import { Lockup } from '../../ui/Lockup';
import { Pill } from '../../ui/DataBits';
import { loc, type Loc } from '../../lib/loc';
import { rpc } from '../admin/api';
import { XsCell, type XsReport } from '../r3/common';
import { periodLabel } from '../../lib/r3format';
import { TopicShares } from '../municipality/MunicipalityHome';

// J6: the privacy method, published without sign-in. Every number on it
// comes from the database (public_method_facts), the same functions that
// enforce them, so the page can't drift from what the system does.

type Facts = {
  privacy_floor: number; council_floor: number; staff_floor: number;
  min_schools_country: number; min_schools_municipality: number; min_school_answers: number;
  what_works_min_actions: number; what_works_min_responses: number;
  question_sets: { version: number; label: string; topics: string[]; validation_status: 'draft' | 'pilot_tested' | 'validated'; validation_note: Loc }[];
};

function PublicFrame({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useI18n();
  useDocumentTitle(title);
  return (
    <div className="statement-page">
      <header className="statement-top">
        <Lockup />
        <LangToggle />
      </header>
      <main className="statement" id="main">
        <p><Link to="/teacher/login">← {t['cp.staffLink']}</Link> · <Link to="/trust">{t['cp.trustTitle']}</Link></p>
        <h1>{title}</h1>
        {children}
      </main>
    </div>
  );
}

export function MethodPage() {
  const { t, lang } = useI18n();
  const [f, setF] = useState<Facts | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    rpc<Facts>('public_method_facts').then((r) => (r.error ? setFailed(true) : setF(r.data)));
  }, []);
  return (
    <PublicFrame title={t['r3.method.title']}>
      <p><Pill kind="warn">{t['ts.draft']}</Pill> {t['r3.method.intro']}</p>
      {failed && <p className="error-text" role="alert">{t['r3.method.failed']}</p>}
      {!f && !failed && <p role="status">{t.loading}</p>}
      {f && (
        <>
          <h2>{t['r3.method.studentsTitle']}</h2>
          <ul>
            <li>{t['r3.method.noId']}</li>
            <li>{t['r3.method.floor'](f.privacy_floor)}</li>
            <li>{t['r3.method.guard']}</li>
            <li>{t['r3.method.complement']}</li>
            <li>{t['r3.method.weeks']}</li>
            <li>{t['r3.method.council'](f.council_floor)}</li>
          </ul>
          <h2>{t['r3.method.staffTitle']}</h2>
          <ul>
            <li>{t['r3.method.staffPrivate']}</li>
            <li>{t['r3.method.staffAgg'](f.staff_floor)}</li>
            <li>{t['r3.method.staffHeads']}</li>
          </ul>
          <h2>{t['r3.method.xsTitle']}</h2>
          <ul>
            <li>{t['r3.method.xsAuth']}</li>
            <li>{t['r3.method.xsFloors'](f.min_schools_municipality, f.min_schools_country, f.min_school_answers)}</li>
            <li>{t['r3.method.xsFrozen']}</li>
            <li>{t['r3.method.ww'](f.what_works_min_actions, f.what_works_min_responses)}</li>
            <li>{t['r3.method.zencera']}</li>
          </ul>
          <h2>{t['r3.method.questionsTitle']}</h2>
          <ul>
            {f.question_sets.map((q) => (
              <li key={q.version}>
                v{q.version} · {q.label}: {q.topics.map((x) => t[`topic.${x}` as 'topic.exams']).join(', ')} — <strong>{t[`r3.qv.${q.validation_status}` as 'r3.qv.draft']}</strong>
                {loc(q.validation_note, lang) && <> ({loc(q.validation_note, lang)})</>}
              </li>
            ))}
          </ul>
          <p><Link to="/insights">{t['r3.pub.title']}</Link></p>
        </>
      )}
    </PublicFrame>
  );
}

type Pub = { id: string; level: string; code: string; area: string | null; period: string; title: Loc; commentary: Loc; data: XsReport; is_draft: boolean; published_at: string };

export function PublicReportsPage() {
  const { t, lang } = useI18n();
  const [list, setList] = useState<Pub[] | null>(null);
  useEffect(() => {
    rpc<Pub[]>('public_period_reports').then((r) => setList(r.error ? [] : r.data));
  }, []);
  return (
    <PublicFrame title={t['r3.pub.title']}>
      <p>{t['r3.pub.intro']} <Link to="/privacy-method">{t['r3.method.title']}</Link></p>
      {!list ? <p role="status">{t.loading}</p> : list.length === 0 ? <p>{t['r3.pub.none']}</p> : list.map((p) => (
        <article key={p.id} className="pub-report">
          <h2>{loc(p.title, lang)} {p.is_draft && <Pill kind="warn">{t['ts.draft']}</Pill>}</h2>
          <p className="text-muted">{p.area ?? t['r3.ww.country']} · {periodLabel(p.period)}</p>
          {loc(p.commentary, lang) && <p>{loc(p.commentary, lang)}</p>}
          <dl className="kv">
            <div><dt>{t['r3.mu.all']}</dt><dd><XsCell w={p.data.total} /></dd></div>
            {(['primary', 'lower_secondary', 'upper_secondary'] as const).map((k) => (
              <div key={k}><dt>{t[`r3.stage.${k}` as 'r3.stage.primary']}</dt><dd><XsCell w={p.data.stages[k]} /></dd></div>
            ))}
          </dl>
          <TopicShares report={p.data} />
        </article>
      ))}
    </PublicFrame>
  );
}
